import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Input,
  Menu,
  type MenuItem,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  createExitHold,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  createDeviceFileGroup,
  deleteDeviceFileGroup,
  listDeviceFileGroups,
  updateDeviceFileGroup,
} from '@/features/devices/api';
import { apiCall } from '@/lib/api';
import { A } from '@solidjs/router';
import { createMutation, createQuery, useQueryClient } from '@tanstack/solid-query';
import {
  ApiClientError,
  type CreateDeviceFileGroupBody,
  type DeviceFileGroupListItem,
  SUPPORTED_CONTENT_TYPES,
  type SupportedContentType,
} from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';

const useFileGroups = () =>
  createQuery(() => ({
    queryKey: ['device-file-groups'] as const,
    queryFn: () => apiCall(listDeviceFileGroups, {}),
  }));

export default function SettingsDeviceFileGroups() {
  const { session } = useAuth();
  const groups = useFileGroups();
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [showCreate, setShowCreate] = createSignal(false);
  const [editing, setEditing] = createSignal<DeviceFileGroupListItem | null>(null);
  // Keep the edit modal mounted (with its group) through the exit animation after close.
  const editHeld = createExitHold(editing);
  const [bannerSlug, setBannerSlug] = createSignal<string | null>(null);
  const [draggingId, setDraggingId] = createSignal<string | null>(null);

  const canManage = () => session()?.permissions.has('devices:files:groups:manage') ?? false;

  const create = createMutation(() => ({
    mutationFn: (body: CreateDeviceFileGroupBody) => apiCall(createDeviceFileGroup, { body }),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['device-file-groups'] });
      setBannerSlug(resp.data.slug);
    },
  }));

  const update = createMutation(() => ({
    mutationFn: (input: { groupId: string; body: Partial<CreateDeviceFileGroupBody> }) =>
      apiCall(updateDeviceFileGroup, { params: { groupId: input.groupId }, body: input.body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-file-groups'] }),
  }));

  const remove = createMutation(() => ({
    mutationFn: (groupId: string) => apiCall(deleteDeviceFileGroup, { params: { groupId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-file-groups'] }),
  }));

  const onDelete = async (g: DeviceFileGroupListItem) => {
    const ok = await confirmDialog({
      title: `Delete group "${g.name}"?`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(g.id);
      toast.success('Group deleted');
    } catch (err) {
      if (err instanceof ApiClientError) toast.error(err.error.message);
      else toast.error('Delete failed');
    }
  };

  // ponytail: single-PATCH reorder; sortOrder collisions fall back to the
  // server's (sortOrder, name) tiebreak — fine for a handful of groups.
  const onDropOn = async (target: DeviceFileGroupListItem) => {
    const sourceId = draggingId();
    setDraggingId(null);
    if (!sourceId || sourceId === target.id) return;
    try {
      await update.mutateAsync({ groupId: sourceId, body: { sortOrder: target.sortOrder } });
    } catch {
      toast.error('Reorder failed');
    }
  };

  const rowMenuItems = (g: DeviceFileGroupListItem): MenuItem[] => {
    const items: MenuItem[] = [{ label: 'Edit', onSelect: () => setEditing(g) }];
    if (!g.isSystem) {
      items.push({ label: 'Delete', danger: true, onSelect: () => onDelete(g) });
    }
    return items;
  };

  return (
    <section>
      <PageHeader
        title="Device file groups"
        actions={
          <Show when={canManage()}>
            <Button onClick={() => setShowCreate(true)}>New group</Button>
          </Show>
        }
      />
      <div class="mx-auto max-w-6xl p-6">
        <Show when={bannerSlug()}>
          <div class="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-900/30">
            New permission keys created — <code>devicefiles:{bannerSlug()}:upload</code> and{' '}
            <code>devicefiles:{bannerSlug()}:delete</code>. They are not granted to any role yet.{' '}
            <A
              href="/admin/roles"
              class="font-medium text-brand-600 hover:underline dark:text-brand-400"
            >
              Go to role permissions
            </A>
          </div>
        </Show>
        <Show when={!groups.isPending} fallback={<Skeleton class="h-40" />}>
          <Show
            when={groups.data}
            fallback={<EmptyState variant="error" title="Failed to load groups" />}
          >
            <Table
              data={groups.data?.data ?? []}
              rowKey={(g) => g.id}
              columns={[
                {
                  key: 'drag',
                  header: '',
                  width: '2rem',
                  render: (g) => (
                    <Show when={canManage()}>
                      <span
                        draggable
                        title="Drag onto another row's handle to reorder"
                        onDragStart={() => setDraggingId(g.id)}
                        onDragEnd={() => setDraggingId(null)}
                        onDragOver={(e) => {
                          if (draggingId()) e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          onDropOn(g);
                        }}
                        class="cursor-grab select-none text-neutral-400"
                        aria-label={`Reorder ${g.name}`}
                      >
                        ⋮⋮
                      </span>
                    </Show>
                  ),
                },
                { key: 'name', header: 'Name', render: (g) => g.name },
                { key: 'slug', header: 'Slug', render: (g) => <code>{g.slug}</code> },
                {
                  key: 'types',
                  header: 'Allowed types',
                  render: (g) => g.allowedContentTypes.join(', '),
                },
                { key: 'cap', header: 'Cap', render: (g) => `${g.perDeviceCap}` },
                {
                  key: 'inUse',
                  header: 'In use',
                  render: (g) =>
                    g.deviceCount > 0
                      ? `${g.deviceCount} device${g.deviceCount === 1 ? '' : 's'}`
                      : '—',
                },
                {
                  key: 'system',
                  header: '',
                  render: (g) => (g.isSystem ? <Badge>system</Badge> : null),
                },
                {
                  key: 'actions',
                  header: '',
                  align: 'right',
                  render: (g) => (
                    <Show when={canManage()}>
                      <Menu items={rowMenuItems(g)} />
                    </Show>
                  ),
                },
              ]}
              empty={
                <EmptyState title="No groups yet" body="System groups are seeded automatically." />
              }
            />
          </Show>
        </Show>
        <GroupModal
          open={showCreate()}
          onClose={() => setShowCreate(false)}
          loading={create.isPending}
          onSubmit={async (body) => {
            await create.mutateAsync(body);
            toast.success('Group created');
            setShowCreate(false);
          }}
        />
        <Show when={editHeld()}>
          {(target) => (
            <GroupModal
              open={editing() != null}
              group={target()}
              onClose={() => setEditing(null)}
              loading={update.isPending}
              onSubmit={async (body) => {
                // System rows: never send slug (server locks it).
                const { slug, ...rest } = body;
                await update.mutateAsync({
                  groupId: target().id,
                  body: target().isSystem ? rest : body,
                });
                toast.success('Group updated');
                setEditing(null);
              }}
            />
          )}
        </Show>
      </div>
    </section>
  );
}

const GroupModal = (props: {
  open: boolean;
  group?: DeviceFileGroupListItem;
  onClose: () => void;
  loading: boolean;
  onSubmit: (body: CreateDeviceFileGroupBody) => Promise<void>;
}) => {
  const [name, setName] = createSignal(props.group?.name ?? '');
  const [slug, setSlug] = createSignal(props.group?.slug ?? '');
  const [cap, setCap] = createSignal(props.group?.perDeviceCap ?? 10);
  const [types, setTypes] = createSignal<SupportedContentType[]>(
    props.group?.allowedContentTypes ?? ['image/jpeg', 'image/png', 'image/webp'],
  );
  const [error, setError] = createSignal<string | null>(null);
  const isEdit = props.group != null;

  const toggleType = (t: SupportedContentType) => {
    setTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  const slugFromName = (n: string) =>
    n
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');

  const onNameInput = (v: string) => {
    setName(v);
    if (!isEdit && (!slug() || slug() === slugFromName(name()))) {
      setSlug(slugFromName(v));
    }
  };

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    if (!name() || !slug() || types().length === 0) {
      setError('Name, slug, and at least one allowed type are required');
      return;
    }
    try {
      await props.onSubmit({
        slug: slug(),
        name: name(),
        perDeviceCap: cap(),
        allowedContentTypes: types(),
      });
      if (!isEdit) {
        setName('');
        setSlug('');
        setCap(10);
        setTypes(['image/jpeg', 'image/png', 'image/webp']);
      }
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.error.message);
      else setError((err as Error).message ?? (isEdit ? 'Update failed' : 'Create failed'));
    }
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title={isEdit ? `Edit group "${props.group?.name}"` : 'New device file group'}
    >
      <form onSubmit={onSubmit} class="flex flex-col gap-4">
        <Input
          label="Name"
          value={name()}
          onInput={(e) => onNameInput(e.currentTarget.value)}
          required
        />
        <Input
          label="Slug"
          value={slug()}
          onInput={(e) => setSlug(e.currentTarget.value)}
          required
          disabled={props.group?.isSystem}
          hint={
            props.group?.isSystem
              ? 'System group slugs are locked.'
              : 'Lowercase, hyphens; used to derive permission keys.'
          }
        />
        <div class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Allowed types</span>
          <div class="flex flex-wrap gap-2">
            <For each={SUPPORTED_CONTENT_TYPES}>
              {(t) => (
                <Checkbox
                  class="rounded-lg border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700"
                  checked={types().includes(t)}
                  onChange={() => toggleType(t)}
                  label={t}
                />
              )}
            </For>
          </div>
        </div>
        <Input
          label="Per-device cap"
          type="number"
          value={String(cap())}
          onInput={(e) => setCap(Number(e.currentTarget.value) || 0)}
        />
        <Show when={error()}>
          <p class="text-xs text-red-600">{error()}</p>
        </Show>
        <div class="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={props.loading}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
